import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from 'src/contexts/userContext';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  PartnerTicketDetail as PartnerTicketDetailType,
  categoryKeys,
  getCategoryDisplayLabel
} from 'src/interfaces/TicketTypes';
import Footer from 'src/components/Footer';
import DocumentList from 'src/components/DocumentList';
import FileUploadButton from 'src/components/FileUploadButton';
import { renderCommentBody } from './commentMarkdown';
import StatusChip from './StatusChip';
import CommentThread from './CommentThread';

function PartnerTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const userCtx = useContext(UserContext);
  const currentUser = userCtx?.currentUser;
  const ticketId = Number(id);

  const [detail, setDetail] = useState<PartnerTicketDetailType | null>(null);
  const [error, setError] = useState('');
  const [docRefreshKey, setDocRefreshKey] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchDetail = () => {
    axiosConfig
      .get('/GetMyTicketDetail', { params: { id: ticketId } })
      .then((res) => setDetail(res.data))
      .catch(() =>
        setError(
          intl.formatMessage({
            id: 'failedToLoadTicket',
            defaultMessage: 'Failed to load ticket.'
          })
        )
      );
  };

  useEffect(() => {
    fetchDetail();
  }, [ticketId]);

  const startEdit = () => {
    if (!detail) return;
    setEditSubject(detail.subject);
    setEditDescription(detail.description);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const saveEdit = async () => {
    if (!editSubject.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      await axiosConfig.put('/UpdateMyTicket', {
        id: ticketId,
        subject: editSubject,
        description: editDescription
      });
      setIsEditing(false);
      fetchDetail();
    } catch {
      setSaveError(
        intl.formatMessage({
          id: 'failedToSaveTicket',
          defaultMessage: 'Failed to save changes.'
        })
      );
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!detail) {
    return (
      <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
        <Typography>
          <FormattedMessage id="loading" defaultMessage="Loading..." />
        </Typography>
      </Container>
    );
  }

  const catKey = categoryKeys[detail.category];
  const categoryDisplay = catKey
    ? intl.formatMessage(catKey)
    : getCategoryDisplayLabel(detail.category, detail.customCategory);

  return (
    <div>
      <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          <FormattedMessage id="back" defaultMessage="Back" />
        </Button>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={
                  isEditing ? (
                    <TextField
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      fullWidth
                      size="small"
                      required
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Typography variant="h4">
                        #{detail.id} — {detail.subject}
                      </Typography>
                      <StatusChip status={detail.status} />
                      {/* Only the creator may edit; everyone else on the
                          installation can read and comment. */}
                      {detail.canEdit && (
                        <Tooltip
                          title={intl.formatMessage({
                            id: 'edit',
                            defaultMessage: 'Edit'
                          })}
                        >
                          <IconButton size="small" onClick={startEdit}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )
                }
              />
              <Divider />
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  <FormattedMessage
                    id="description"
                    defaultMessage="Description"
                  />
                </Typography>
                {isEditing ? (
                  <>
                    <TextField
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      fullWidth
                      multiline
                      minRows={4}
                    />
                    {saveError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {saveError}
                      </Alert>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        mt: 1
                      }}
                    >
                      <Button onClick={cancelEdit} disabled={saving}>
                        <FormattedMessage id="cancel" defaultMessage="Cancel" />
                      </Button>
                      <Button
                        variant="contained"
                        onClick={saveEdit}
                        disabled={saving || !editSubject.trim()}
                      >
                        <FormattedMessage id="save" defaultMessage="Save" />
                      </Button>
                    </Box>
                  </>
                ) : (
                  renderCommentBody(detail.description || '')
                )}

                <Box sx={{ mt: 2 }}>
                  <DocumentList
                    ticketId={detail.id}
                    refreshKey={docRefreshKey}
                    canDelete
                    deleteOwnerId={currentUser?.id}
                  />
                  <Box sx={{ mt: 1 }}>
                    <FileUploadButton
                      scope={0}
                      ticketId={detail.id}
                      onUploaded={() => setDocRefreshKey((k) => k + 1)}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <CommentThread
              ticketId={detail.id}
              comments={detail.comments}
              onCommentAdded={fetchDetail}
              adminUsers={detail.participants}
              canDelete={false}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader
                title={
                  <FormattedMessage id="details" defaultMessage="Details" />
                }
              />
              <Divider />
              <CardContent
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                      id="installation"
                      defaultMessage="Installation"
                    />
                  </Typography>
                  <Typography variant="body2">
                    {detail.installationName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="status" defaultMessage="Status" />
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip status={detail.status} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="category" defaultMessage="Category" />
                  </Typography>
                  <Typography variant="body2">
                    {detail.customCategory || categoryDisplay}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="openedBy" defaultMessage="Opened by" />
                  </Typography>
                  <Typography variant="body2">
                    {detail.createdByName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="createdAt" defaultMessage="Created" />
                  </Typography>
                  <Typography variant="body2">
                    {new Date(detail.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="updatedAt" defaultMessage="Updated" />
                  </Typography>
                  <Typography variant="body2">
                    {new Date(detail.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </div>
  );
}

export default PartnerTicketDetail;
